import React, { type ComponentProps, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';

import { ContentfulImage } from '../Image';

// ─────────────────────────────────────────────────────────────────────────────

export const BrandIcon = (props: Props) => {
  const { src, alt, size = DEFAULT_ICON_SIZE, style } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const iconStyle = useStyle(() => ({ width: size, height: size }), [size]);

  // ─────────────────────────────────────────────────────────────────────

  const imageOptions = useMemo<ContentfulImageOptions>(
    () => ({ fm: 'webp', h: size * IMAGE_SCALE, w: size * IMAGE_SCALE }),
    [size],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View
      entering={FadeIn.duration(theme.transitions.short)}
      pointerEvents="none"
      style={[styles.container, iconStyle, style]}
    >
      <ContentfulImage
        baseUrl={src}
        alt={alt}
        contentFit="contain"
        options={imageOptions}
        style={[styles.image, iconStyle]}
      />
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_ICON_SIZE = 34;
const IMAGE_SCALE = 1.5;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    height: DEFAULT_ICON_SIZE,
    width: DEFAULT_ICON_SIZE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  src: string;
  alt: string | undefined;
  size?: number;
  style?: ComponentProps<typeof Animated.View>['style'];
};

type ContentfulImageOptions = ComponentProps<typeof ContentfulImage>['options'];
